import { Dispatch, FC, ReactNode, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { TableSkeleton, TextSkeleton } from '@/lib/components';
import { ActivityCleanFilterIcon } from '@/lib/icon';
import { Button, Flex, FloatModal, InputSearch, Spinner } from '@/lib/v2/components';

import ErrorBoundary from '@/src/application/components/ErrorBoundary';
import configData from '@/src/config.json';
import { ContactsGroupModal, ContactsTagModal } from '@/src/ContactsModule/components';
import { quantifier } from '@/src/ContactsModule/utils/quantifier';
import { ServiceMethods } from '@/src/infrastructure/Protocol/EmblueService';
import { useEmblue, useService } from '@/src/infrastructure/Protocol/useEmblue';
import { ITableNpsContact } from '@/src/modules/ContactsModule/screens/HappinessMain/types';
import { showNotification } from '@/src/utils/ShowToastNotification';

import {
  useHappinessMutationContext,
  useHappinessStateContext,
} from '@/modules/ContactsModule/contexts/HappinessContext';
import { HappinessTable } from '@/modules/ContactsModule/screens/HappinessMain/components/HappinessContactsInvolved/components/HappinessTable';
import { HappinessTableActionBar } from '@/modules/ContactsModule/screens/HappinessMain/components/HappinessContactsInvolved/components/HappinessTableActionBar';

type HappinessTableContainerProps = {
  setCountMessage?: Dispatch<SetStateAction<ReactNode | undefined>>;
};

export const HappinessTableContainer: FC<HappinessTableContainerProps> = ({ setCountMessage }) => {
  const navigate = useNavigate();
  const service = useService();
  const { t } = useTranslation();
  const {
    npsTotalContactsCount,
    happinessFilter,
    isHappinessFilterApplied,
    npsContacts,
    searchQuery,
    isLoadingContacts,
    searchQueryHandler,
    totalContactsBySearch,
    pageSize,
    isAllResultsSelected,
    selectedContacts,
    showSelectAllResultsButton,
  } = useHappinessStateContext();
  const {
    setShowSelectAllResultsButton,
    setOnSearch,
    handleOnChangeSearchQuery,
    handleClearSearchQuery,
    resetOnSearch,
    setIsAllResultsSelected,
  } = useHappinessMutationContext();

  const [isChecked, setIsChecked] = useState(false);

  const [groups] = useEmblue(ServiceMethods.getGroups, { identifier: 'GROUPS_DROP_DOWN' });
  const [tags] = useEmblue(ServiceMethods.getTagsDropDown, { identifier: 'TAGS_DROP_DOWN' });
  const [flowsLists] = useEmblue(ServiceMethods.getFlowsByAccount);

  const [selectedGroups, setSelectedGroups] = useState<number[]>([]);
  const [selectedTags, setSelectedTags] = useState<number[]>([]);
  const [showSpinnerByAction, setShowSpinnerByAction] = useState(false);
  /***** Hooks to handle contactActionBar clicks ******/
  const [flowId, setFlowId] = useState(0);
  const [confirmToFlow, setConfirmToFlow] = useState(false);
  const [showGroupsModal, setShowGroupsModal] = useState(false);
  const [showTagsModal, setShowTagsModal] = useState(false);
  /***** FIN Hooks to handle contactActionBar clicks ******/
  const [selectedContactsCountToAction, setSelectedContactsCountToAction] = useState<number>(
    configData.CONTACTS_ACTIONS.maxContactsToAction
  );
  const [maxGroupsToAdd, setMaxGroupsToAdd] = useState<number>();
  const [maxTagsToAdd, setMaxTagsToAdd] = useState<number>();
  const groupsListAux = groups?.list?.filter((g) => g && g.group && g.group.id).map((g) => g.group);

  useEffect(() => {
    if (!setCountMessage) return;
    let countMessage: string | JSX.Element;
    //Waiting countOfSearch api response
    if (isLoadingContacts) {
      countMessage = <TextSkeleton />;
    }
    //No results case
    else if (!searchQuery && !isHappinessFilterApplied && !npsTotalContactsCount) {
      countMessage = t('noResultsFound');
    }
    //No filters applied case
    else if (!searchQuery && !isHappinessFilterApplied) {
      countMessage = <span>{t('CONTACTS_MAIN.noFilters')}</span>;
    }
    //Any filter applied case
    else if (
      (searchQuery && totalContactsBySearch) ||
      (happinessFilter.length && totalContactsBySearch)
    ) {
      countMessage = (
        <span>
          {' '}
          {t('CONTACTS_MAIN.youHave')}{' '}
          <span className="font-medium text-[#004DBC]"> {quantifier(totalContactsBySearch)} </span>{' '}
          {totalContactsBySearch === 1
            ? t('CONTACTS_MAIN.contactsMatchSingular')
            : t('CONTACTS_MAIN.contactsMatch')}
        </span>
      );
    }
    //No results case
    else {
      countMessage = t('noResultsFound');
    }
    setCountMessage(countMessage);
  }, [
    happinessFilter.length,
    isHappinessFilterApplied,
    isLoadingContacts,
    npsTotalContactsCount,
    searchQuery,
    setCountMessage,
    totalContactsBySearch,
  ]);

  useEffect(() => {
    if (!isChecked && selectedContacts.length > 0) {
      setIsChecked(true);
    }
    if (isChecked && selectedContacts.length === 0) {
      setIsChecked(false);
      setIsAllResultsSelected(false);
    }
    setShowSelectAllResultsButton(selectedContacts.length === pageSize);
    setSelectedContactsCountToAction(
      npsContacts?.count
        ? npsContacts?.count <= configData.CONTACTS_ACTIONS.maxContactsToAction
          ? npsContacts?.count
          : configData.CONTACTS_ACTIONS.maxContactsToAction
        : npsTotalContactsCount &&
          npsTotalContactsCount <= configData.CONTACTS_ACTIONS.maxContactsToAction
        ? npsTotalContactsCount
        : configData.CONTACTS_ACTIONS.maxContactsToAction
    );
  }, [selectedContacts]);

  useEffect(() => {
    if (selectedGroups.length >= 1) {
      addContactsToGroups().catch(console.error);
    }
  }, [selectedGroups]);

  useEffect(() => {
    if (selectedTags.length >= 1) {
      addContactsToTags().catch(console.error);
    }
  }, [selectedTags]);

  useEffect(() => {
    if (confirmToFlow && flowId) {
      addContactToFlow(flowId).catch(console.error);
      setConfirmToFlow(false);
    }
  }, [confirmToFlow, flowId]);

  useEffect(() => {
    if (isAllResultsSelected) {
      const maxGroups = Math.floor(
        configData.CONTACTS_ACTIONS.maxRowsGroups / selectedContactsCountToAction
      );
      const maxTags = Math.floor(
        configData.CONTACTS_ACTIONS.maxRowsTags / selectedContactsCountToAction
      );
      setMaxGroupsToAdd(maxGroups === 0 ? 1 : maxGroups);
      setMaxTagsToAdd(maxTags === 0 ? 1 : maxTags);
    } else {
      setMaxGroupsToAdd(undefined);
      setMaxTagsToAdd(undefined);
    }
  }, [isAllResultsSelected]);

  // TODO: Refactor this to avoid refresh the entire window
  const redirectToView = (path: string, withRefresh?: boolean) => {
    navigate(path);
    withRefresh && navigate(0);
  };

  const getContactsIds = useCallback(
    (list?: ITableNpsContact[]) => {
      const listAux: ITableNpsContact[] =
        list ?? (npsContacts as { list: ITableNpsContact[] }).list;
      const contactsIds: number[] =
        selectedContacts.length === pageSize
          ? listAux.map((c) => c.contact.emailId) // all ids of page
          : listAux.filter((c, i) => selectedContacts.includes(i)).map((c) => c.contact.emailId); // ids of some contacts in page
      return contactsIds;
    },
    [npsContacts, pageSize, selectedContacts]
  );

  const addContactsToGroups = async () => {
    if (npsContacts === undefined || groups === undefined || selectedGroups.length === 0) return;
    setShowSpinnerByAction(true);
    let result: boolean | undefined;
    if (isAllResultsSelected) {
      result = await service?.addAllContactsToGroup({
        groupsIds: selectedGroups,
        search: searchQuery,
        tagsFilter: [],
        groupsFilter: [],
        statusFilter: [],
        happinessFilter,
        segmentsFilter: 0,
        discardedFilter: [],
        resultCount: selectedContactsCountToAction,
      });
    } else {
      const contactsIds = getContactsIds();
      result = await service?.addContactsToGroup({
        contactsIds: contactsIds,
        groupsIds: selectedGroups,
      });
    }
    setShowSpinnerByAction(false);
    showNotification(result ? result : false);
    if (result) {
      if (selectedGroups.length === 1)
        redirectToView(`/v2/contacts/groups/${selectedGroups.at(0)}`, true);
      else redirectToView('/v2/contacts/groups');
    }
  };

  const addContactsToTags = async () => {
    if (npsContacts === undefined) return;
    setShowSpinnerByAction(true);
    let result: boolean | undefined;
    if (isAllResultsSelected) {
      result = await service?.associateTagsToAllContacts({
        tagsIds: selectedTags,
        search: searchQuery,
        tagsFilter: [],
        groupsFilter: [],
        statusFilter: [],
        happinessFilter,
        segmentsFilter: 0,
        discardedFilter: [],
        resultCount: selectedContactsCountToAction,
      });
    } else {
      const contactsIds = getContactsIds();
      result = await service?.addContactsToTags({
        contactsIds: contactsIds,
        tagsIds: selectedTags,
      });
    }
    setShowSpinnerByAction(false);
    showNotification(result ? result : false);
    if (result) {
      redirectToView('/v2/contacts/tags');
    }
  };

  const addContactToFlow = async (flowsId: number) => {
    if (npsContacts === undefined) return;
    setShowSpinnerByAction(true);
    let result: boolean | undefined;
    if (isAllResultsSelected) {
      result = await service?.addFlowsToAllContacts({
        flowId: flowsId,
        search: searchQuery,
        tagsFilter: [],
        groupsFilter: [],
        statusFilter: [],
        happinessFilter,
        segmentsFilter: 0,
        discardedFilter: [],
        resultCount: selectedContactsCountToAction,
      });
    } else {
      const contactsIds = getContactsIds();
      result = await service?.addFlowsToContacts({ flowId: flowId, contactsIds: contactsIds });
    }
    setShowSpinnerByAction(false);
    showNotification(result ? result : false);
    if (result) {
      redirectToView(`/v2/contacts${window.location.search}`);
    }
  };

  return (
    <ErrorBoundary>
      <Flex column>
        <Flex alignment="start">
          {!isChecked ? (
            <div className="z-10 px-5 pt-4">
              <Flex alignment="start" itemAlignment="center">
                <Flex
                  withGap
                  alignment="start"
                  className="mb-[-44px] xl:max-w-[900px] 2xl:max-w-fit"
                >
                  {npsTotalContactsCount && npsTotalContactsCount > 0 ? (
                    <>
                      <div className="w-[400px]">
                        <InputSearch
                          placeHolder={t('CONTACTS_FILTERS.searchInputBar')}
                          value={searchQueryHandler}
                          onAction={setOnSearch}
                          onChange={handleOnChangeSearchQuery}
                          onClear={handleClearSearchQuery}
                        />
                      </div>
                      <div className="w-auto">
                        <Button
                          fullWidth
                          standard
                          iconLeft={<ActivityCleanFilterIcon color="#004DBC" />}
                          id="clear-filters-button"
                          tooltip={t('CONTACTS_FILTERS.cleanAllFilters')}
                          onClick={resetOnSearch}
                        ></Button>
                      </div>
                    </>
                  ) : (
                    <></>
                  )}
                </Flex>
              </Flex>
            </div>
          ) : (
            <HappinessTableActionBar
              contactSelection={
                isAllResultsSelected ? selectedContactsCountToAction : selectedContacts.length
              }
              flowsLists={typeof flowsLists?.list === 'string' ? flowsLists.list : []}
              setConfirmToFlow={setConfirmToFlow}
              setFlowId={setFlowId}
              setShowGroupsModal={setShowGroupsModal}
              setShowTagsModal={setShowTagsModal}
            />
          )}
        </Flex>
        {showSelectAllResultsButton && (
          <Flex alignment="center">
            {!isAllResultsSelected ? (
              <div>
                <span>
                  {t('CONTACTS_ACTIONS_DROPDOWN.All')} <b>{pageSize}</b>{' '}
                  {t('CONTACTS_ACTIONS_DROPDOWN.ContactsPageSelected')}
                </span>
                <span
                  aria-hidden="true"
                  className="ml-1 cursor-pointer rounded-md p-2 font-medium text-[#004dbc] hover:bg-slate-50"
                  onClick={() => setIsAllResultsSelected(true)}
                >
                  {selectedContactsCountToAction ===
                  configData.CONTACTS_ACTIONS.maxContactsToAction ? (
                    <span title={t('CONTACTS_ACTIONS_DROPDOWN.WorkingOnIncreasingNumber')}>
                      {t('CONTACTS_ACTIONS_DROPDOWN.SelectFirst')}{' '}
                      {quantifier(selectedContactsCountToAction)}{' '}
                      {t('CONTACTS_ACTIONS_DROPDOWN.ContactsMatchSearch')}
                    </span>
                  ) : (
                    <span>
                      {t('CONTACTS_ACTIONS_DROPDOWN.SelectAll')}{' '}
                      {quantifier(selectedContactsCountToAction)}{' '}
                      {t('CONTACTS_ACTIONS_DROPDOWN.ContactsMatchSearch')}
                    </span>
                  )}
                </span>
              </div>
            ) : (
              <div>
                <span>
                  {t('CONTACTS_ACTIONS_DROPDOWN.All')}{' '}
                  <b>{quantifier(selectedContactsCountToAction)}</b>{' '}
                  {t('CONTACTS_ACTIONS_DROPDOWN.ContactsMatchSearch')}{' '}
                  {t('CONTACTS_ACTIONS_DROPDOWN.AreSelected')}
                </span>
                <span
                  aria-hidden="true"
                  className="ml-1 cursor-pointer rounded-md p-2 font-medium text-[#004dbc] hover:bg-slate-50"
                  onClick={() => setIsAllResultsSelected(false)}
                >
                  {t('CONTACTS_ACTIONS_DROPDOWN.ClearSelection')}
                </span>
              </div>
            )}
          </Flex>
        )}
        {!npsContacts && (
          <div className="h-full w-full py-10">
            <TableSkeleton />
          </div>
        )}
        {npsContacts && <HappinessTable />}
        {showGroupsModal && (
          <ContactsGroupModal
            groups={groupsListAux ?? [{ id: 0, groupName: '' }]}
            maxGroupsToAdd={maxGroupsToAdd}
            setSelectedGroups={setSelectedGroups}
            setShowGroupsModal={setShowGroupsModal}
          />
        )}
        {showTagsModal && (
          <ContactsTagModal
            maxTagsToAdd={maxTagsToAdd}
            setSelectedTags={setSelectedTags}
            setShowTagsModal={setShowTagsModal}
            tags={tags === undefined ? [{ id: 0, name: '' }] : tags.list}
          />
        )}
        {showSpinnerByAction && (
          <FloatModal>
            <Spinner />
          </FloatModal>
        )}
      </Flex>
    </ErrorBoundary>
  );
};
